<template>
  <section class="product-characteristics">
    <header>
      Параметры сырья
      <VButton
        v-if="!hideEdit"
        theme="link"
        @click="$emit('edit-characteristics')"
      >
        Редактировать
      </VButton>
    </header>

    <dl
      v-for="productCharacteristic in product.characteristics"
      :key="productCharacteristic.characteristic.id"
    >
      <dt class="label">
        {{ productCharacteristic.name }}:&nbsp;
      </dt>

      <!-- Картинки: миниатюры, каждая открывается в новом окне по клику -->
      <dd
        v-if="productCharacteristic.isImage"
        class="pictures"
      >
        <template v-if="$core.utils.isNotEmptyArray(productCharacteristic.entityPointerValues)">
          <VPictureThumbnail
            v-for="pointer in productCharacteristic.entityPointerValues"
            :key="pointer.id"
            :entity-pointer="pointer"
            size="medium"
          />
        </template>
        <template v-else>не указано</template>
      </dd>

      <!-- Ссылки на товары: поставщику не показываем, чужие товары не его дело -->
      <dd
        v-else-if="productCharacteristic.isEntityLink"
        class="entity-links"
      >
        <template v-if="$core.currentPartner.isSeller">--</template>
        <template v-else-if="$core.utils.isNotEmptyArray(productCharacteristic.entityPointerValues)">
          <VLink
            v-for="pointer in productCharacteristic.entityPointerValues"
            :key="pointer.id"
            class="ellipsis"
            @click="$core.dialogs.ProductCard.showAsPromise({ id: pointer.id })"
          >
            {{ pointer.name }}
          </VLink>
        </template>
        <template v-else>не указано</template>
      </dd>

      <!-- Ссылка строкой: поставщику тоже не показываем -->
      <dd v-else-if="productCharacteristic.isLink"
          class="links">
        <template v-if="$core.currentPartner.isSeller">--</template>
        <template v-else>
          <VLink
            @click="onProductCharLinkClick(characteristicValue(productCharacteristic))"
            class="ellipsis"
          > {{ characteristicValue(productCharacteristic) }} </VLink>

         </template>
      </dd>

      <dd v-else>
        {{ characteristicValue(productCharacteristic) }}
      </dd>
    </dl>

    <footer>
      <VButton
        v-if="!hideEdit"
        theme="link"
        @click="$emit('edit-characteristics')"
      >
        Редактировать
      </VButton>
    </footer>
  </section>
</template>

<style lang="scss" src="./ProductCharacteristicsReadModeBlock.scss"/>

<script lang="ts">
import type Product               from '@/domains/Products/Product';
import type ProductCharacteristic from '@/domains/Products/ProductCharacteristic';
import type EntityPointer         from '@/domains/EntityPointer/EntityPointer';
import Characteristic             from '@/domains/Characteristics/Characteristic';

import VPictureThumbnail from '@/lib/components/VPictureThumbnail/VPictureThumbnail.vue';

/**
 * Характеристики товара в режиме просмотра.
 *
 * Три способа показать значение: картинки — миниатюрами, ссылки на товары —
 * кликабельным списком, остальное — строкой. У офера эти два типа поставщик
 * не заполняет, поэтому там достаточно строки; у товара их заполняет
 * покупатель, и показывать надо по-настоящему.
 *
 * Ссылки — и на товар, и строкой — поставщику не показываются вовсе.
 */
export default defineComponent({
  name: 'ProductCharacteristicsReadModeBlock',

  components: {
    VPictureThumbnail,
  },

  props: {
    product: {
      type:     Object as PropType<Product>,
      required: true,
    },
    hideEdit: {
      type:    Boolean,
      default: false,
    },
  },

  emits: ['edit-characteristics'],

  methods: {
    characteristicValue(item: ProductCharacteristic): string {
      const unitName = item.characteristic?.unit?.name;
      const unitSuffix = unitName && unitName !== 'строка' && unitName !== 'Число'
        ? unitName
        : '';

      switch (item.valueType) {
        case Characteristic.VALUE_TYPE_STRING:
        case Characteristic.VALUE_TYPE_LINK: {
          if (item.stringValue == null || item.stringValue === '') return 'не указано';
          return unitSuffix ? `${item.stringValue} ${unitSuffix}` : item.stringValue;
        }
        case Characteristic.VALUE_TYPE_NUMERIC: {
          return item.numericValue?.toString(unitSuffix, 'не указано') ?? 'не указано';
        }
        case Characteristic.VALUE_TYPE_SELECTABLE_SINGLE:
        case Characteristic.VALUE_TYPE_SELECTABLE_MULTI: {
          if (!Array.isArray(item.listValue) || item.listValue.length === 0) return 'не указано';
          const names = item.listValue.map(v => v.name).join(', ');
          return unitSuffix ? `${names} ${unitSuffix}` : names;
        }
        default:
          return 'не указано';
      }
    },

    showLinkedProduct(pointer: EntityPointer): void {
      this.$core.dialogs.ProductCard.showAsPromise({ id: pointer.id });
    },

    onProductCharLinkClick(link: string) {
      window.open(link, '_blank')
    },
  },
});
</script>
